import React, { useState, useEffect, useCallback, useMemo } from 'react';


import {
  fetchAllUsers,
  createUser,
  updateUser,
  deleteUser,
  fetchAdminUsers,
} from '../services/usersService';
import { User } from '../models/user';
import { useAuth } from '../context/AuthContext';
import { useUserDataAuthConsumer } from '../context/UserDataContext';
import {
  AIRLINES,
  LABELS,
  BUTTON_LABELS,
  AIRLINE_DISPLAY_NAMES,
} from '../constants/appConstants';
import '../styles/AdminAllowedGroups.scss';

const getErrorMessageFromApi = (err: unknown, fallbackMessage: string, conflictMessage: string): string => {
  if (typeof err !== 'object' || err === null) {
    return fallbackMessage;
  }

  const errorWithResponse = err as {
    response?: {
      status?: number;
      data?: {
        message?: string;
        error?: string;
        title?: string;
      } | string;
    };
    message?: string;
  };

  const statusCode = errorWithResponse.response?.status;
  const responseData = errorWithResponse.response?.data;
  const apiMessage = typeof responseData === 'string'
    ? responseData
    : responseData?.message || responseData?.error || responseData?.title;

  if (statusCode === 409) {
    return apiMessage || conflictMessage;
  }

  if (apiMessage) {
    return apiMessage;
  }

  if (errorWithResponse.message) {
    return errorWithResponse.message;
  }

  return fallbackMessage;
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<User>({
    email: '',
    airlines: '',
    admin: false,
    isActive: true,
  });
  const [selectedAirlines, setSelectedAirlines] = useState<string[]>([]);
  const [adminUsers, setAdminUsers] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [modalFeedback, setModalFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const userGroups = useAuth();
  const token = userGroups?.userData?.access_token || '';
  const userData = useUserDataAuthConsumer();

  const allowedAdminUsers = useMemo(() => new Set(adminUsers), [adminUsers]);
  const userEmail = useMemo(
    () => userData?.email || '',
    [userData?.email]
  );
  const isAdmin = useMemo(() => {
    const userEmailLower = userEmail.toLowerCase();
    const adminEmailsLowerCase = new Set(Array.from(allowedAdminUsers).map(email => email.toLowerCase()));
    return adminEmailsLowerCase.has(userEmailLower);
  }, [allowedAdminUsers, userEmail]);
  const isAuthLoading = userGroups.isLoading;

  // Client-side pagination
  const totalPages = Math.ceil(users.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return users.slice(startIndex, endIndex);
  }, [users, currentPage, itemsPerPage]);

  // Reset to page 1 when users change
  useEffect(() => {
    setCurrentPage(1);
  }, [users.length]);

  useEffect(() => {
    if (!isAuthLoading && token) {
      loadAdminUsers();
    }
  }, [isAuthLoading, token]);

  useEffect(() => {
    if (isAdmin && !isAuthLoading && token) {
      loadUsers();
    }
  }, [isAdmin, isAuthLoading, token]);

  const loadAdminUsers = useCallback(async () => {
    try {
      const admins = await fetchAdminUsers(token);
      setAdminUsers(admins);
    } catch (err) {
      console.error('Failed to load admin users:', err);
      setAdminUsers([]);
    }
  }, [token]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllUsers(token);
      setUsers(data);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleOpenModal = useCallback((user?: User) => {
    setModalFeedback(null);
    if (user) {
      setEditingUser(user);
      setFormData(user);
      setSelectedAirlines(user.airlines ? user.airlines.split(',').map(a => a.trim()) : []);
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        airlines: '',
        admin: false,
        isActive: true,
      });
      setSelectedAirlines([]);
    }
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingUser(null);
    setModalFeedback(null);
    setSelectedAirlines([]);
    setFormData({
      email: '',
      airlines: '',
      admin: false,
      isActive: true,
    });
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setModalFeedback(null);

    const airlinesString = selectedAirlines.join(',');
    const dataToSave = { ...formData, airlines: airlinesString };

    try {
      if (editingUser?.id) {
        await updateUser(token, editingUser.id, dataToSave);
        setModalFeedback({ type: 'success', message: 'User updated successfully.' });
      } else {
        await createUser(token, dataToSave);
        setModalFeedback({ type: 'success', message: 'User created successfully.' });
      }
      await loadUsers();
      globalThis.setTimeout(() => {
        handleCloseModal();
      }, 1200);
    } catch (err) {
      setModalFeedback({
        type: 'error',
        message: getErrorMessageFromApi(
          err,
          'Failed to save user',
          'User already exists.'
        ),
      });
      console.error(err);
    }
  }, [selectedAirlines, formData, editingUser, token, loadUsers, handleCloseModal]);

  const handleDelete = useCallback(async (id: number) => {
    if (!globalThis.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    setError(null);
    try {
      await deleteUser(token, id);
      await loadUsers();
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
    }
  }, [token, loadUsers]);

  const handleToggleActive = useCallback(async (user: User) => {
    if (!user.id) return;

    try {
      await updateUser(token, user.id, {
        ...user,
        isActive: !user.isActive,
      });
      await loadUsers();
    } catch (err) {
      setError('Failed to update user status');
      console.error(err);
    }
  }, [token, loadUsers]);

  const toggleAirlineSelection = useCallback((airline: string) => {
    setSelectedAirlines(prev =>
      prev.includes(airline)
        ? prev.filter(a => a !== airline)
        : [...prev, airline]
    );
  }, []);



  if (isAuthLoading) {
    return (
      <div className="overview-wrapper">
        <div className="admin-allowed-groups">
          <div className="loading">{LABELS.CHECKING_PERMISSIONS}</div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-content-wrapper">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
          <p>Your email: {userEmail || 'Unknown'}</p>
          <p>Only specific authorized users can access the Admin Users page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-wrapper">
      <div className="admin-allowed-groups">
          <div className="admin-header">
            <div>
              <h1>Manage Users</h1>
            </div>
            <button 
              className="btn-primary" 
              onClick={() => handleOpenModal()}
              type="button"
            >
              + Add New User
            </button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <div className="filter-section">
            <span className="group-count">
              {users.length} user(s) found
            </span>
          </div>

          {loading ? (
            <div className="loading">{LABELS.LOADING}</div>
          ) : (
            <>
              <div className="groups-container">
                <div className="groups-section">
                  <table className="groups-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Airlines</th>
                        <th>Admin</th>
                        <th>Is Active</th>
                        <th>Last Modified By</th>
                        <th>Date Time</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map(user => (
                        <tr key={user.id}>
                          <td>{user.email}</td>
                          <td>{user.airlines}</td>
                          <td>
                            <span className={user.admin ? 'status-active' : 'status-inactive'}>
                              {user.admin ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            <label className="switch" aria-label={`Toggle ${user.email} status`}>
                              <input
                                type="checkbox"
                                checked={user.isActive}
                                onChange={() => handleToggleActive(user)}
                                aria-label={`${user.isActive ? 'Active' : 'Inactive'} status`}
                              />
                              <span className="slider"></span>
                            </label>
                            <span className={user.isActive ? 'status-active' : 'status-inactive'}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>{user.lastModifiedBy || '-'}</td>
                          <td>{user.dateTime ? new Date(user.dateTime).toLocaleString() : '-'}</td>
                          <td className="actions">
                            <button 
                              className="btn-edit" 
                              onClick={() => handleOpenModal(user)}
                              type="button"
                            >
                              {BUTTON_LABELS.EDIT}
                            </button>
                            <button 
                              className="btn-delete" 
                              onClick={() => user.id && handleDelete(user.id)}
                              type="button"
                            >
                              {BUTTON_LABELS.DELETE}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {paginatedUsers.length === 0 && (
                  <div className="no-groups">
                    <p>No users found. Click "Add New User" to create one.</p>
                  </div>
                )}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages} ({users.length} total)
                  </span>
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          {isModalOpen && (
            <div 
              className="modal-overlay" 
              onClick={handleCloseModal}
              onKeyDown={(e) => e.key === 'Escape' && handleCloseModal()}
              role="presentation"
              tabIndex={-1}
            >
              <div 
                className="modal-content" 
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    handleCloseModal();
                  }
                }}
                role="dialog"
                aria-modal="true"
                tabIndex={0}
              >
                <div className="modal-header">
                  <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
                  <button className="modal-close" onClick={handleCloseModal} type="button">✕</button>
                </div>
                <form onSubmit={handleSubmit}>
                  {modalFeedback && (
                    <div
                      className={`alert ${modalFeedback.type === 'error' ? 'alert-error' : 'alert-success'}`}
                      role="alert"
                      aria-live="polite"
                    >
                      {modalFeedback.message}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="user@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <fieldset>
                      <legend>Airlines *</legend>
                      <div className="checkbox-group">
                        {Object.values(AIRLINES).map(airline => (
                          <label key={airline} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={selectedAirlines.includes(airline)}
                              onChange={() => toggleAirlineSelection(airline)}
                            />
                            {AIRLINE_DISPLAY_NAMES[airline] || airline} ({airline})
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <small className="form-hint">Select at least one airline</small>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.admin}
                        onChange={(e) => setFormData({ ...formData, admin: e.target.checked })}
                      />
                      Admin Privileges
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      Active
                    </label>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                      {BUTTON_LABELS.CANCEL}
                    </button>
                    <button type="submit" className="btn-primary" disabled={selectedAirlines.length === 0}>
                      {editingUser ? BUTTON_LABELS.UPDATE : BUTTON_LABELS.CREATE}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default AdminUsers;
